if (!customElements.get('bumpino-boutique')) {
  customElements.define('bumpino-boutique', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.garments = Array.from(this.querySelectorAll('[data-bb-garment]'));
      this.buttons = Array.from(this.querySelectorAll('[data-bb-select]'));
      this.panels = Array.from(this.querySelectorAll('[data-bb-panel]'));
      this.track = this.querySelector('[data-bb-track]');
      this.previous = this.querySelector('[data-bb-prev]');
      this.next = this.querySelector('[data-bb-next]');
      this.counter = this.querySelector('[data-bb-count]');
      this.dots = Array.from(this.querySelectorAll('[data-bb-dot]'));
      this.swings = new Map();
      this.dialog = this.querySelector('[data-bb-dialog]');
      this.dialogContent = this.querySelector('[data-bb-dialog-content]');
      if (!this.garments.length || !this.track || !this.dialog) return;
      this.listen(this, 'click', event => {
        const button = event.target.closest('button');
        if (!button || !this.contains(button)) return;
        const index = this.buttons.indexOf(button);
        if (index !== -1) {
          if (Date.now() < (this.suppressClickUntil || 0)) return;
          const wasSelected = index === this.index;
          this.select(index);
          if (!this.isMobile() || wasSelected) this.openExploration(button);
        } else if (button.hasAttribute('data-bb-dot')) {
          this.select(Number(button.dataset.bbDot));
        } else if (button.hasAttribute('data-bb-explore')) {
          this.openExploration(button);
        } else if (button === this.previous) {
          this.select(this.index - 1);
        } else if (button === this.next) {
          this.select(this.index + 1);
        } else if (button.hasAttribute('data-bb-close')) {
          this.closeExploration();
        } else if (button.hasAttribute('data-bb-view')) {
          this.showView(button.dataset.bbView);
        }
      });
      this.buttons.forEach((button, index) => {
        this.listen(button, 'focus', () => {
          if (button.matches(':focus-visible') && !this.dialog.open) this.select(index);
        });
        this.listen(button, 'pointerenter', () => {
          if (!this.isMobile() && matchMedia('(hover: hover) and (pointer: fine)').matches && !this.dialog.open) this.select(index);
        });
      });
      this.listen(this.track, 'keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const forward = event.key === (this.isRTL() ? 'ArrowLeft' : 'ArrowRight');
        const index = event.key === 'Home' ? 0 : event.key === 'End' ? this.garments.length - 1 : this.index + (forward ? 1 : -1);
        this.select(index);
        this.buttons[this.index].focus({preventScroll: true});
      });
      this.listen(this.track, 'pointerdown', event => {
        this.gesture = null;
        if (!this.isMobile() || event.pointerType === 'mouse' || event.isPrimary === false) return;
        this.gesture = {id: event.pointerId, x: event.clientX, y: event.clientY, horizontal: false};
      });
      this.listen(this.track, 'pointermove', event => {
        const start = this.gesture;
        if (!start || start.id !== event.pointerId) return;
        const dx = event.clientX - start.x;
        const dy = event.clientY - start.y;
        if (!start.horizontal && (Math.abs(dx) < 12 || Math.abs(dx) < Math.abs(dy) * 1.4)) return;
        if (!start.horizontal) {
          start.horizontal = true;
          this.track.setPointerCapture?.(event.pointerId);
          this.track.classList.add('is-dragging');
        }
        this.suppressClickUntil = Date.now() + 450;
        const limit = (this.track.clientWidth || 375) * .32;
        const blocked = (this.index === 0 && (this.isRTL() ? dx < 0 : dx > 0)) || (this.index === this.garments.length - 1 && (this.isRTL() ? dx > 0 : dx < 0));
        this.track.style.setProperty('--bb-drag', `${Math.max(-limit, Math.min(limit, dx)) * (blocked ? .2 : .8)}px`);
      });
      this.listen(this.track, 'pointerup', event => {
        const start = this.gesture;
        this.resetDrag();
        if (!start || start.id !== event.pointerId) return;
        const dx = event.clientX - start.x;
        const dy = event.clientY - start.y;
        if (start.horizontal) this.suppressClickUntil = Date.now() + 450;
        if (Math.abs(dx) < Math.max(36, (this.track.clientWidth || 375) * .12) || Math.abs(dx) < Math.abs(dy) * 1.4) return;
        this.suppressClickUntil = Date.now() + 450;
        const forward = this.isRTL() ? dx > 0 : dx < 0;
        this.select(this.index + (forward ? 1 : -1));
      });
      this.listen(this.track, 'pointercancel', () => this.resetDrag());
      this.listen(this.track, 'lostpointercapture', () => this.resetDrag());
      this.listen(this.dialog, 'cancel', event => { event.preventDefault(); this.closeExploration(); });
      this.listen(this.dialog, 'keydown', event => {
        if (event.key === 'Escape') { event.preventDefault(); this.closeExploration(); }
      });
      this.listen(this.dialog, 'close', () => {
        if (this.opener?.isConnected) this.opener.focus({preventScroll: true});
        this.opener = null;
        this.classList.remove('is-exploring');
      });
      if (typeof IntersectionObserver === 'function') {
        this.visibilityObserver = new IntersectionObserver(entries => {
          this.classList.toggle('is-in-view', entries[0].isIntersecting);
        }, {threshold: .1});
        this.visibilityObserver.observe(this.track);
      } else {
        this.classList.add('is-in-view');
      }
      this.listen(document, 'visibilitychange', () => this.classList.toggle('is-page-hidden', document.hidden));
      this.querySelectorAll('[data-bb-atlas]').forEach(atlas => this.prepareAtlas(atlas));
      this.select(Number.isInteger(this.index) ? this.index : this.garments.length >= 3 ? 2 : 0);
      this.classList.add('is-ready');
    }
    listen(target, type, callback, signal = this.controller.signal) {
      target.addEventListener(type, callback, {signal});
    }
    isRTL() {
      return getComputedStyle(this).direction === 'rtl' || document.documentElement.dir === 'rtl';
    }
    isMobile() {
      return matchMedia('(max-width: 767px)').matches;
    }
    resetDrag() {
      const id = this.gesture?.id;
      this.gesture = null;
      this.track.classList.remove('is-dragging');
      this.track.style.setProperty('--bb-drag', '0px');
      if (id != null && this.track.hasPointerCapture?.(id)) this.track.releasePointerCapture(id);
    }
    prepareAtlas(atlas, signal = this.controller.signal) {
      const image = atlas.querySelector('img');
      if (!image) return;
      if (!atlas.classList.contains('is-posed')) {
        image.classList.add('bb__atlas-front');
        const side = image.cloneNode(false);
        side.classList.remove('bb__atlas-front');
        side.classList.add('bb__atlas-side');
        side.alt = '';
        side.setAttribute('aria-hidden', 'true');
        atlas.append(side);
        atlas.classList.add('is-posed');
      }
      const measure = () => {
        if (image.naturalWidth && image.naturalHeight) atlas.style.aspectRatio = `${image.naturalWidth / 2} / ${image.naturalHeight}`;
      };
      const fallback = () => {
        atlas.hidden = true;
        const replacement = atlas.parentElement.querySelector('[data-bb-fallback]');
        if (replacement) replacement.hidden = false;
      };
      this.listen(image, 'load', measure, signal);
      this.listen(image, 'error', fallback, signal);
      if (image.complete) {
        if (image.naturalWidth) measure();
        else fallback();
      }
    }
    select(index) {
      const previousIndex = this.index;
      this.index = Math.max(0, Math.min(this.garments.length - 1, index));
      const total = this.garments.length + 1.2;
      let preceding = 0;
      this.garments.forEach((garment, i) => {
        const active = i === this.index;
        const weight = active ? 2.2 : 1;
        garment.classList.toggle('is-active', active);
        garment.style.setProperty('--bb-place', `${(preceding + weight / 2) / total * 100}%`);
        garment.style.setProperty('--bb-distance', String(i - this.index));
        garment.style.zIndex = String(active ? 4 : 3 - Math.min(2, Math.abs(i - this.index)));
        garment.style.setProperty('--bb-rhythm', `${i * .08}s`);
        preceding += weight;
        this.buttons[i].setAttribute('aria-pressed', String(active));
        this.buttons[i].style.setProperty('--bb-distance', String(i - this.index));
      });
      this.panels.forEach((panel, i) => { panel.hidden = i !== this.index; });
      this.previous.disabled = this.index === 0;
      this.next.disabled = this.index === this.garments.length - 1;
      this.counter.textContent = `${this.index + 1} / ${this.garments.length}`;
      this.dots.forEach((dot, i) => dot.setAttribute('aria-pressed', String(i === this.index)));
      if (previousIndex !== this.index && previousIndex != null && this.isMobile() && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        const direction = (this.index > previousIndex ? -1 : 1) * (this.isRTL() ? -1 : 1);
        this.garments.forEach((garment, i) => {
          if (Math.abs(i - this.index) > 1) return;
          const suspension = garment.querySelector('.bb__suspension');
          if (!suspension?.animate) return;
          this.swings.get(suspension)?.cancel();
          const angle = direction * (i === this.index ? 2.2 : 1.1);
          const swing = suspension.animate([1, -.65, .4, -.2, .08, 0].map((value, j) => ({transform: `rotate(${value * angle}deg)`, offset: j / 5})), {duration: 1900 + i * 65, easing: 'ease-in-out'});
          this.swings.set(suspension, swing);
        });
      }
    }
    openExploration(opener) {
      const template = this.panels[this.index].querySelector('[data-bb-exploration]');
      if (!template || typeof this.dialog.showModal !== 'function') return;
      this.opener = opener;
      this.dialogController?.abort();
      this.dialogController = new AbortController();
      this.dialogContent.replaceChildren(template.content.cloneNode(true));
      const title = this.dialogContent.querySelector('[data-bb-dialog-title]');
      if (title) {
        title.id = `${this.dialog.id || 'BoutiqueDialog'}-title`;
        this.dialog.setAttribute('aria-labelledby', title.id);
      }
      this.dialogContent.querySelectorAll('[data-bb-atlas]').forEach(atlas => this.prepareAtlas(atlas, this.dialogController.signal));
      this.showView('full');
      if (!this.dialog.open) this.dialog.showModal();
      this.classList.add('is-exploring');
      this.dialog.querySelector('[data-bb-close]').focus({preventScroll: true});
    }
    showView(view) {
      const target = Array.from(this.dialogContent.querySelectorAll('[data-bb-focus-view]')).find(figure => figure.dataset.bbFocusView === view);
      if (!target) return;
      this.dialogContent.querySelectorAll('[data-bb-focus-view]').forEach(figure => { figure.hidden = figure !== target; });
      this.dialogContent.querySelectorAll('[data-bb-view]').forEach(button => { button.setAttribute('aria-pressed', String(button.dataset.bbView === view)); });
    }
    closeExploration() {
      if (this.dialog.open) this.dialog.close();
    }
    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      this.controller?.abort();
      this.dialogController?.abort();
      this.dialogController = null;
      this.visibilityObserver?.disconnect();
      this.swings?.forEach(swing => swing.cancel());
      this.swings?.clear();
      this.controller = null;
      this.resetDrag();
      this.suppressClickUntil = 0;
    }
  });
}
